import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, AlertTitle, Autocomplete, Box, Button, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton, MenuItem, TextField, Tooltip
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import { confirmDialog } from "../../components/popups/ConfirmDialog";
import styles from "./VReference.module.css";
import NetworkManager from "../../app/managers/networkManager";
import {
    VREFERENCE_STATUSES,
    addVReference, checkVReferenceDuplicates, clearAttachments, clearDuplicates,
    deleteVReferenceAttachment, listVReferenceAttachments, updateVReference,
    uploadVReferenceAttachment
} from "./vreferenceSlice";

const ACCEPTED_TYPES = ".pdf,.docx"
const MAX_UPLOAD_BYTES = 10 * 1000 * 1000

const EMPTY_FORM = {
    first_name: "", last_name: "", email: "", phone: "",
    company: "", notes: "", id_poste: null, poste_label: "", status: "new", hr_notes: ""
}

/**
 * Add / edit a V-Reference contact.
 *
 * Two things here are more than a form field and are worth knowing about:
 *
 *  1. **The post is a freeSolo Autocomplete.** Picking an option sends `id_poste`; typing
 *     something the referential does not contain sends `poste_label`. Never both — see
 *     onSubmit. That is the whole reason the table carries two columns for one concept.
 *
 *  2. **Duplicate detection warns, it never blocks.** Two collabs can legitimately know the
 *     same person, so the confirmation lives entirely here: the first Save turns into "Save
 *     anyway", the second posts an ordinary add. The backend has no `force` flag and does not
 *     consult the check. And for a user who only holds `view`, it deliberately returns
 *     the owner's name and date instead of the record itself, so the warning is actionable
 *     without exposing a contact they have no rights over — hence the two shapes handled in
 *     renderDuplicates.
 *
 * A CV can only be attached to a contact that exists, so on creation the file is held in
 * `pendingFile` and uploaded once the insert returns an id.
 */
export default function VReferenceModal({ show, contact, onHide, onSaved, fullScreen, canSeeHrFields }) {

    const dispatch = useDispatch()
    const fileInputRef = useRef(null)

    const postes = useSelector((state) => state.vreference.postes)
    const attachments = useSelector((state) => state.vreference.attachments)
    const duplicates = useSelector((state) => state.vreference.duplicates)
    const lang = useSelector((state) => state.settings.lang)

    const [form, setForm] = useState(EMPTY_FORM)
    const [pendingFile, setPendingFile] = useState(null)
    const [acknowledgedDuplicates, setAcknowledgedDuplicates] = useState(false)
    const [errors, setErrors] = useState({})

    const isEditing = !!(contact && contact.id)

    useEffect(() => {
        if (!show) return

        dispatch(clearDuplicates())
        dispatch(clearAttachments())
        setPendingFile(null)
        setAcknowledgedDuplicates(false)
        setErrors({})

        if (contact && contact.id) {
            setForm({
                first_name: contact.first_name || "",
                last_name: contact.last_name || "",
                email: contact.email || "",
                phone: contact.phone || "",
                company: contact.company || "",
                notes: contact.notes || "",
                id_poste: contact.id_poste || null,
                poste_label: contact.poste_label || "",
                // Absent from the payload entirely for a non-HR user — the backend does not
                // select them — so these fall back to the empty defaults and are never sent.
                status: contact.status || "new",
                hr_notes: contact.hr_notes || ""
            })
            dispatch(listVReferenceAttachments({ id_vreference: contact.id }))
        } else {
            setForm(EMPTY_FORM)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, contact])

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }))
        // Any change to an identifying field invalidates a warning the user already saw.
        if (field === 'email' || field === 'phone') {
            setAcknowledgedDuplicates(false)
            dispatch(clearDuplicates())
        }
    }

    /** Runs on blur rather than on every keystroke — one lookup per finished field. */
    const onIdentityBlur = () => {
        if (!form.email && !form.phone) return
        dispatch(checkVReferenceDuplicates({
            email: form.email || undefined,
            phone: form.phone || undefined,
            id: isEditing ? contact.id : undefined
        }))
    }

    const validate = () => {
        const nextErrors = {}
        if (!form.first_name.trim()) nextErrors.first_name = Localize("VREFERENCE_REQUIRED", lang)
        if (!form.last_name.trim()) nextErrors.last_name = Localize("VREFERENCE_REQUIRED", lang)
        // A contact nobody can reach is not a contact — the backend enforces this too.
        if (!form.email.trim() && !form.phone.trim()) {
            nextErrors.email = Localize("VREFERENCE_CONTACT_REQUIRED", lang)
        }
        if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
            nextErrors.email = Localize("VREFERENCE_INVALID_EMAIL", lang)
        }
        setErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }

    const onPickFile = (event) => {
        const file = event.target.files && event.target.files[0]
        if (!file) return
        if (file.size > MAX_UPLOAD_BYTES) {
            toast.error(Localize("VREFERENCE_FILE_TOO_LARGE", lang))
            return
        }
        setPendingFile(file)
    }

    const uploadPending = async (idVReference) => {
        if (!pendingFile) return true
        const response = await dispatch(uploadVReferenceAttachment({ file: pendingFile, idVReference }))
        const data = response.payload && response.payload.responseData
        if (!data || data.status !== 1) {
            toast.error(Localize("VREFERENCE_UPLOAD_FAILED", lang))
            return false
        }
        // The backend reports whether it could read text out of the file. A scanned CV
        // uploads fine but is not searchable, and silently pretending otherwise would make
        // the search look broken later.
        toast.success(Localize(data.indexed ? "VREFERENCE_CV_INDEXED" : "VREFERENCE_CV_STORED_ONLY", lang))
        setPendingFile(null)
        return true
    }

    const onSubmit = async () => {
        if (!validate()) return

        if (duplicates.length && !acknowledgedDuplicates) {
            setAcknowledgedDuplicates(true)
            toast.warn(Localize("VREFERENCE_CONFIRM_DUPLICATE", lang))
            return
        }

        // id_poste and poste_label are mutually exclusive by construction: a picked option
        // has an id, a typed one does not. Sending both would leave the row claiming two
        // different job titles.
        const payload = {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            company: form.company.trim() || null,
            notes: form.notes || null,
            id_poste: form.id_poste || null,
            poste_label: form.id_poste ? null : (form.poste_label || null)
        }

        // Only ever sent by someone who may set them. The backend drops them from a
        // non-HR payload anyway (sanitizePayload); not sending them keeps the request
        // honest about what it is asking for.
        if (canSeeHrFields) {
            payload.hr_notes = form.hr_notes || null
            if (isEditing) payload.status = form.status
        }

        const response = isEditing
            ? await dispatch(updateVReference({ ...payload, id: contact.id }))
            : await dispatch(addVReference(payload))

        const data = response.payload && response.payload.responseData
        if (!data || data.status !== 1) {
            toast.error(Localize("VREFERENCE_ACTION_FAILED", lang))
            return
        }

        const idVReference = isEditing ? contact.id : data.id
        await uploadPending(idVReference)

        toast.success(Localize(isEditing ? "VREFERENCE_UPDATED" : "VREFERENCE_ADDED", lang))
        onSaved()
    }

    const confirmDeleteAttachment = (attachment) => {
        confirmDialog({
            title: <Langs str="DELETE_FILE" />,
            message: <Langs str="DELETE_FILE_MSG" />,
            danger: true,
            onConfirm: () => onDeleteAttachment(attachment),
        })
    }

    const onDeleteAttachment = async (attachment) => {
        const response = await dispatch(deleteVReferenceAttachment({ id: attachment.id }))
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            dispatch(listVReferenceAttachments({ id_vreference: contact.id }))
        } else {
            toast.error(Localize("VREFERENCE_ACTION_FAILED", lang))
        }
    }

    const renderDuplicates = () => {
        if (!duplicates.length) return null
        return (
            <Alert severity="warning" className={styles.duplicateAlert}>
                <AlertTitle className={styles.duplicateTitle}>
                    <Langs str="VREFERENCE_DUPLICATE_TITLE" />
                </AlertTitle>
                {duplicates.map((duplicate, index) => (
                    <div key={duplicate.id || `restricted-${index}`} className={styles.duplicateRow}>
                        {duplicate.restricted
                            // The caller may not see this contact. Enough to act on, nothing more.
                            ? <Langs str="VREFERENCE_DUPLICATE_RESTRICTED"
                                params={{
                                    owner: duplicate.created_by_name || "-",
                                    date: duplicate.created_at
                                        ? new Date(duplicate.created_at).toLocaleDateString() : "-"
                                }} />
                            : <Langs str="VREFERENCE_DUPLICATE_VISIBLE"
                                params={{
                                    name: `${duplicate.first_name} ${duplicate.last_name}`,
                                    owner: duplicate.created_by_name || "-"
                                }} />}
                    </div>
                ))}
                <span className={styles.duplicateHint}>
                    <Langs str="VREFERENCE_DUPLICATE_HINT" />
                </span>
            </Alert>
        )
    }

    const selectedPoste = postes.find(p => p.id === form.id_poste) || null

    // The mobile view reuses this component rather than duplicating 300 lines of form: the
    // fields, the validation and the duplicate handling are identical, only the presentation
    // differs, and a second copy would drift the moment one of the three changes.
    return (
        <Dialog open={!!show} onClose={onHide} maxWidth="md" fullWidth fullScreen={!!fullScreen}
            classes={{ paper: `${styles.vreference} ${styles.dialogPaper}` }}>

            <DialogTitle className={styles.modalTitle}>
                <Langs str={isEditing ? "VREFERENCE_EDIT_TITLE" : "VREFERENCE_ADD_TITLE"} />
                <IconButton size="small" onClick={onHide}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers>

                {renderDuplicates()}

                <Box className={styles.formGrid}>
                    <TextField
                        label={<Langs str="VREFERENCE_FIRST_NAME" />}
                        value={form.first_name}
                        onChange={(e) => setField('first_name', e.target.value)}
                        error={!!errors.first_name}
                        helperText={errors.first_name}
                        size="small" required fullWidth
                    />
                    <TextField
                        label={<Langs str="VREFERENCE_LAST_NAME" />}
                        value={form.last_name}
                        onChange={(e) => setField('last_name', e.target.value)}
                        error={!!errors.last_name}
                        helperText={errors.last_name}
                        size="small" required fullWidth
                    />
                    <TextField
                        label={<Langs str="VREFERENCE_EMAIL" />}
                        value={form.email}
                        onChange={(e) => setField('email', e.target.value)}
                        onBlur={onIdentityBlur}
                        error={!!errors.email}
                        helperText={errors.email}
                        size="small" fullWidth
                    />
                    <TextField
                        label={<Langs str="VREFERENCE_PHONE" />}
                        value={form.phone}
                        onChange={(e) => setField('phone', e.target.value)}
                        onBlur={onIdentityBlur}
                        size="small" fullWidth
                    />

                    {/* freeSolo: pick from the referential, or type a title it does not have. */}
                    <Autocomplete
                        freeSolo
                        options={postes}
                        getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || '')}
                        value={selectedPoste || form.poste_label || null}
                        onChange={(event, value) => {
                            if (value && typeof value === 'object') {
                                setForm(prev => ({ ...prev, id_poste: value.id, poste_label: "" }))
                            } else {
                                setForm(prev => ({ ...prev, id_poste: null, poste_label: value || "" }))
                            }
                        }}
                        onInputChange={(event, value, reason) => {
                            if (reason === 'input') {
                                setForm(prev => ({ ...prev, id_poste: null, poste_label: value }))
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} size="small"
                                label={<Langs str="VREFERENCE_POSTE" />} />
                        )}
                        slotProps={{ paper: { className: styles.vreference } }}
                        fullWidth
                    />

                    <TextField
                        label={<Langs str="VREFERENCE_COMPANY" />}
                        value={form.company}
                        onChange={(e) => setField('company', e.target.value)}
                        size="small" fullWidth
                    />
                </Box>

                <TextField
                    label={<Langs str="VREFERENCE_NOTES" />}
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                    size="small" fullWidth multiline minRows={3}
                    className={styles.notesField}
                    helperText={<Langs str="VREFERENCE_NOTES_HINT" />}
                />

                <Divider className={styles.divider} />

                <span className={styles.sectionTitle}>
                    <Langs str="VREFERENCE_CV_SECTION" />
                </span>

                <div className={styles.attachmentsRow}>
                    <Button
                        className={`${styles.pillButton} ${styles.pillButtonSmall}`}
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                        <Langs str="VREFERENCE_UPLOAD_CV" />
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_TYPES}
                        hidden
                        onChange={onPickFile}
                    />
                    <span className={styles.uploadHint}>
                        <Langs str="VREFERENCE_UPLOAD_HINT" />
                    </span>
                </div>

                {pendingFile ? (
                    <Chip
                        className={styles.pendingChip}
                        label={Localize("VREFERENCE_PENDING_UPLOAD", lang)
                            .replace("%name%", pendingFile.name)}
                        onDelete={() => setPendingFile(null)}
                    />
                ) : null}

                <div className={styles.attachmentList}>
                    {attachments.map(attachment => (
                        <div key={attachment.id} className={styles.attachmentItem}>
                            <span className={styles.attachmentName}>{attachment.original_name}</span>
                            <span className={styles.attachmentMeta}>
                                {attachment.created_by_name} · {new Date(attachment.created_at).toLocaleDateString()}
                            </span>
                            <Tooltip title={Localize("VREFERENCE_OPEN_CV", lang)}>
                                <IconButton
                                    size="small"
                                    onClick={() => window.open(
                                        NetworkManager.getInstance().getVReferenceAttachmentUrl(attachment.id),
                                        '_blank', 'noopener')}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={Localize("DELETE", lang)}>
                                <IconButton size="small" onClick={() => confirmDeleteAttachment(attachment)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ))}
                    {!attachments.length && !pendingFile ? (
                        <span className={styles.emptyAttachments}>
                            <Langs str="VREFERENCE_NO_CV" />
                        </span>
                    ) : null}
                </div>

                {/*
                  * HR review — invisible to the collaborator who made the referral, however
                  * their own the contact is. The status is the state of the hiring process and
                  * `hr_notes` is HR's private assessment; showing either to the referrer would
                  * turn a friendly introduction into a channel for relaying a rejection.
                  * The backend never sends these fields to a non-HR user, so this block has
                  * nothing to render for them.
                  */}
                {canSeeHrFields ? (
                    <>
                        <Divider className={styles.divider} />

                        <span className={styles.sectionTitle}>
                            <Langs str="VREFERENCE_HR_SECTION" />
                        </span>
                        <span className={styles.hrOnlyBadge}>
                            <LockOutlinedIcon className={styles.hrOnlyIcon} fontSize="inherit" />
                            <Langs str="VREFERENCE_HR_ONLY" />
                        </span>

                        <div className={styles.hrBlock}>
                            {isEditing ? (
                                <TextField
                                    select
                                    label={<Langs str="VREFERENCE_STATUS" />}
                                    value={form.status}
                                    onChange={(e) => setField('status', e.target.value)}
                                    size="small"
                                    className={styles.statusField}
                                    SelectProps={{ MenuProps: { classes: { paper: styles.vreference } } }}
                                >
                                    {VREFERENCE_STATUSES.map(status => (
                                        <MenuItem key={status} value={status}>
                                            <Langs str={`VREFERENCE_STATUS_${status.toUpperCase()}`} />
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : null}

                            <TextField
                                label={<Langs str="VREFERENCE_HR_NOTES" />}
                                value={form.hr_notes}
                                onChange={(e) => setField('hr_notes', e.target.value)}
                                size="small" fullWidth multiline minRows={3}
                                helperText={<Langs str="VREFERENCE_HR_NOTES_HINT" />}
                            />
                        </div>
                    </>
                ) : null}

            </DialogContent>

            <DialogActions className={styles.dialogActions}>
                <Button
                    className={`${styles.pillButton} ${styles.pillButtonNeutral}`}
                    onClick={onHide}
                >
                    <Langs str="CANCEL" />
                </Button>
                <Button className={styles.pillButton} onClick={onSubmit}>
                    <Langs str={duplicates.length && !acknowledgedDuplicates
                        ? "VREFERENCE_SAVE_ANYWAY" : "SAVE"} />
                </Button>
            </DialogActions>
        </Dialog>
    )
}
